import type { JSchema } from '../types';
import type { AddressBookRecordValidationSchema } from './address-book';
import type { ContractValidationSchema } from './contract';
import type { SpotQuoteCreateValidationSchema, SpotQuoteEditValidationSchema } from './spot-quote';
import type { SpotQuoteRateValidationSchema } from './spot-quote-rate';

export const VALIDATION_SCHEMA_KEYS = {
  addressBook: {
    record: 'AddressBookRecordSchemaDefinition',
  },
  contract: {
    create: 'ContractSchemaDefinition',
  },
  routingGuide: {
    create: 'CreateRoutingGuideSchemaDefinition',
  },
  spotQuote: {
    create: 'CreateQuote',
    edit: 'EditQuote',
    read: 'GetQuote',
  } as const,
  spotQuoteRate: {
    read: (codeId: string) => `GetRatesQuoteRateCriteria:${codeId}` as const,
  },
} as const;

type FlattenObject<T> = {
  [K in keyof T]: T[K] extends (...args: any) => infer R ? R : T[K] extends object ? FlattenObject<T[K]> : T[K];
}[keyof T];

export type ValidationSchemaKey = Prettify<FlattenObject<typeof VALIDATION_SCHEMA_KEYS>>;

export interface ValidationSchemaApi {
  customerKey?: number;
  schemaKey: ValidationSchemaKey;
  validationSchema: string; // JSON string
  enabledFrom?: string;
  enabledTo?: string;
  createOn?: string;
  modifiedOn?: string;
  lookupCodeId?: string;
}

export interface PricingValidationSchemaApi {
  id: string;
  customerId?: number | null;
  schemaName: ValidationSchemaKey;
  validationSchema: string; // JSON string
  enabledFrom?: string | null;
  enabledTo?: string | null;
  createdBy?: number | null;
  createdOn?: string | null;
  modifiedBy?: number | null;
  modifiedOn?: string | null;
}

export interface GenericValidationSchema<Key extends ValidationSchemaKey, S extends JSchema>
  extends Omit<ValidationSchemaApi, 'validationSchema'> {
  schemaKey: Key;
  validationSchema: S;
}

export type AllSchemas =
  | GenericValidationSchema<'AddressBookRecordSchemaDefinition', AddressBookRecordValidationSchema>
  | GenericValidationSchema<'ContractSchemaDefinition', ContractValidationSchema>
  | GenericValidationSchema<'GetQuote', SpotQuoteCreateValidationSchema>
  | GenericValidationSchema<'CreateQuote', SpotQuoteCreateValidationSchema>
  | GenericValidationSchema<'EditQuote', SpotQuoteEditValidationSchema>
  | GenericValidationSchema<`GetRatesQuoteRateCriteria:${string}`, SpotQuoteRateValidationSchema>;

export type ValidationSchemas = Record<ValidationSchemaKey, AllSchemas>;

export type ValidationSchema = NonNullable<ValidationSchemas[keyof ValidationSchemas]>;
